/* eslint-disable import/prefer-default-export */

import moment from "moment";
import { formatPriceV2 } from "helpers/commonHelpers";
import { ApprovalLogTypes, ApprovalLogActionTypes } from "../../../../constants";
import { getNameAndEmailFromString } from "../components/ApprovalFlow/components/ApprovalFlowHelpers";
import { ApprovalUpdateItemStatusType } from "../components/ApprovalFlow/components/ApprovalUpdateItem/ApprovalUpdateItemStatus";
import ReviewForApproval from "../components/ApprovalFlow/components/ReviewForApproval";
import ApproverNames from "../components/ApprovalFlow/components/ApproverNames";

type ApprovalTimelineData = {
  title: string;
  date?: string;
  variant?: "inactive" | "active" | "completed";
  showLine?: boolean;
  children?: ApprovalUpdateItemData[];
};

type ApprovalUpdateItemData = {
  title: string;
  titleRenderer?: React.ReactNode;
  subTitle?: string;
  status?: ApprovalUpdateItemStatusType;
  additionalData?: Record<string, any>;
  date?: string;
  comment?: string;
  action?: React.ReactNode;
};

type ApprovalDataFormatOptions = {
  approvalLogs: any;
  quotationData: any;
  currentUserEmail: string;
};

const canUserPerformAction = (approvalLog: any, quotationData: any, currentUserEmail: string): boolean => {
  if (!approvalLog || !quotationData) return false;

  const currentQuotation = quotationData.quotations?.find(
    (quotation: any) => quotation.reference === approvalLog?.quotation_detail?.reference_number,
  );

  if (!currentQuotation) return false;

  const { general, actions, status } = currentQuotation;

  const isQuoteExpired = moment(general.quote_expiration, "YYYY-MM-DD HH:mm:ss ZZ").isBefore(moment());
  const hasApprovePermission = (actions.can_approve || actions.can_preapprove) && status === "require_approval";
  const isQuoteOpen = ![
    "booked",
    "booked_as_duplicate",
    "lost",
    "canceled",
    "canceled_by_provider",
    "confirmation_needed",
  ].includes(status);

  const requestedApprovers = approvalLog?.requested_approvers?.map((approver: string) =>
    getNameAndEmailFromString(approver),
  );
  const isCurrentUserInRequestedApprovers = requestedApprovers.find((approver: any) => approver.email === currentUserEmail);

  return !isQuoteExpired && hasApprovePermission && isQuoteOpen && !!isCurrentUserInRequestedApprovers;
};

const isActionUserIsInPreviousApprovalList = (actionUser: string, previousApprovalString: string): boolean => {
  const previousApprovalList = previousApprovalString.split(", ");
  if (!previousApprovalList.length) return false;

  return previousApprovalList.includes(actionUser);
};

const prepareBookingRequestedData = (approvalLog: any): ApprovalTimelineData => {
  const { currency_code, total_quoted_amount, company_name } = approvalLog?.quotation_detail || {};

  return {
    title: "Booking requested",
    date: approvalLog.timestamp,
    variant: "completed",
    showLine: true,
    children: [
      {
        title: `${currency_code} ${formatPriceV2(total_quoted_amount)} • ${company_name}`,
        subTitle: `Requested by ${approvalLog?.user_full_name}`,
        comment: approvalLog?.comment,
      },
    ],
  };
};

export const formatApprovalFlowData = (options: ApprovalDataFormatOptions): ApprovalTimelineData[] => {
  const { approvalLogs, quotationData, currentUserEmail } = options;
  const data: ApprovalTimelineData[] = [];
  if (!approvalLogs) return data;

  const approvalLogsData = approvalLogs.slice().reverse();

  approvalLogsData.forEach((approvalLog: any, index: number) => {
    if (
      approvalLog.action_type === ApprovalLogActionTypes.PRE_BOOKING &&
      [ApprovalLogTypes.SUGGESTED_OR_REQUESTED_APPROVAL, ApprovalLogTypes.PRE_APPROVAL].includes(approvalLog.action) &&
      (!data.length ||
        [ApprovalLogTypes.APPROVED, ApprovalLogTypes.REJECTED, ApprovalLogTypes.CANCELED].includes(
          approvalLogsData?.[index - 1]?.action,
        ))
    ) {
      data.push(prepareBookingRequestedData(approvalLog));
      data.push({
        title: "Pending approval",
        variant: "active",
        showLine: true,
        children: [],
      });
    }

    if (approvalLog.action_type === ApprovalLogActionTypes.PRE_BOOKING && !!data?.at(-1)?.children) {
      if ([ApprovalLogTypes.SUGGESTED_OR_REQUESTED_APPROVAL, ApprovalLogTypes.PRE_APPROVAL].includes(approvalLog.action)) {
        if (
          data?.at(-1)?.children?.at(-1) &&
          isActionUserIsInPreviousApprovalList(approvalLog.user_full_name, data.at(-1)!.children!.at(-1)!.title)
        ) {
          data.at(-1)!.children!.at(-1)!.status = "approved";
          data.at(-1)!.children!.at(-1)!.date = approvalLog.timestamp;
          if (data.at(-1)!.children!.at(-1)?.action) {
            data.at(-1)!.children!.at(-1)!.action = null;
          }
        }
        const addActionButton = canUserPerformAction(approvalLog, quotationData, currentUserEmail);
        data.at(-1)!.children!.push({
          title: approvalLog?.requested_approvers
            ?.map((approver: string) => getNameAndEmailFromString(approver).name)
            ?.join(", "),
          titleRenderer: <ApproverNames approvalLog={approvalLog} />,
          status: approvalLog.is_transferred ? "approval_transferred" : "pending_approval",
          ...(approvalLog.is_transferred &&
            approvalLog.comments?.length > 0 &&
            approvalLog.comments.at(-1)?.transferred_users?.length > 0 && {
              additionalData: {
                transferredUsers: approvalLog.comments
                  .at(-1)
                  ?.transferred_users?.map((approver: string) => getNameAndEmailFromString(approver).name)
                  ?.join(", "),
              },
            }),
          ...(addActionButton && { action: <ReviewForApproval /> }),
        });
        if (approvalLog.is_transferred && approvalLog.comments?.length > 0) {
          const transferredUsers = approvalLog.comments.slice(0, -1).reverse();
          transferredUsers.forEach((comment: any) => {
            if (comment.transferred_users?.length > 0) {
              const transferredUsersNames = comment.transferred_users
                .map((approver: string) => getNameAndEmailFromString(approver).name)
                ?.join(", ");

              data.at(-1)!.children!.push({
                title: comment.commenter,
                titleRenderer: comment.commenter,
                status: "approval_transferred",
                date: comment.submit_date,
                comment: comment.comment,
                additionalData: {
                  transferredUsers: transferredUsersNames,
                },
              });
            }
          });
        }
      } else if (
        [ApprovalLogTypes.APPROVED, ApprovalLogTypes.REJECTED, ApprovalLogTypes.CANCELED].includes(approvalLog.action)
      ) {
        if (
          data?.at(-1)?.children?.at(-1) &&
          !["approved", "canceled", "rejected", "approval_transferred"].includes(data?.at(-1)?.children?.at(-1)?.status!)
        ) {
          if (approvalLog.action === ApprovalLogTypes.APPROVED) {
            data.at(-1)!.title = "Approved";
            data.at(-1)!.variant = "completed";
            data.at(-1)!.children!.at(-1)!.status = "approved";
            data.at(-1)!.children!.at(-1)!.date = approvalLog.timestamp;
            data.at(-1)!.children!.at(-1)!.comment = approvalLog?.comment;
            if (!isActionUserIsInPreviousApprovalList(approvalLog.user_full_name, data.at(-1)!.children!.at(-1)!.title)) {
              data.at(-1)!.children!.at(-1)!.title = approvalLog?.user_full_name;
              data.at(-1)!.children!.at(-1)!.titleRenderer = approvalLog?.user_full_name;
            }
          } else if (approvalLog.action === ApprovalLogTypes.CANCELED) {
            data.at(-1)!.title = "Approval request canceled";
            data.at(-1)!.variant = "completed";
            data.at(-1)!.children!.at(-1)!.status = "canceled";
            data.at(-1)!.children!.at(-1)!.title = approvalLog?.user_full_name;
            data.at(-1)!.children!.at(-1)!.titleRenderer = approvalLog?.user_full_name;
            data.at(-1)!.children!.at(-1)!.date = approvalLog.timestamp;
            data.at(-1)!.children!.at(-1)!.comment = approvalLog?.comment;
          } else {
            data.at(-1)!.title = "Approval rejected";
            data.at(-1)!.variant = "completed";
            data.at(-1)!.children!.at(-1)!.status = "rejected";
            data.at(-1)!.children!.at(-1)!.date = approvalLog.timestamp;
            data.at(-1)!.children!.at(-1)!.comment = approvalLog?.comment;
            if (!isActionUserIsInPreviousApprovalList(approvalLog.user_full_name, data.at(-1)!.children!.at(-1)!.title)) {
              data.at(-1)!.children!.at(-1)!.title = approvalLog?.user_full_name;
              data.at(-1)!.children!.at(-1)!.titleRenderer = approvalLog?.user_full_name;
            }
          }
          if (data.at(-1)!.children!.at(-1)?.action) {
            data.at(-1)!.children!.at(-1)!.action = null;
          }
        } else if (data?.at(-1)?.children?.at(-1) && data?.at(-1)?.children?.at(-1)?.status! === "approval_transferred") {
          data.at(-1)!.title = approvalLog.action === ApprovalLogTypes.APPROVED ? "Approved" : "Approval rejected";
          data.at(-1)!.variant = "completed";
          data.at(-1)!.children!.push({
            title: approvalLog.user_full_name,
            titleRenderer: approvalLog.user_full_name,
            status: approvalLog.action === ApprovalLogTypes.APPROVED ? "approved" : "rejected",
            date: approvalLog.timestamp,
            comment: approvalLog.comment,
          });
        }
      }
    }
  });

  const directBookingLog = approvalLogsData.find((approvalLog: any) => approvalLog.action_display === "Booked");

  if (data?.at(-1)?.children && data?.at(-1)?.children?.at(-1)?.status) {
    if (data.at(-1)!.children!.at(-1)!.status === "approved" || !!directBookingLog) {
      data.push({
        title: "Booked",
        showLine: false,
        date: data.at(-1)!.children!.at(-1)!.date,
        variant: "completed",
        ...(!!directBookingLog && {
          children: [
            {
              title: directBookingLog.user_full_name,
              status: "booked",
              comment: directBookingLog?.comment,
              date: directBookingLog.timestamp,
            },
          ],
        }),
      });
    } else if (["rejected", "canceled"].includes(data.at(-1)!.children!.at(-1)!.status!)) {
      data.push({
        title: "Pending booking request",
        showLine: false,
        variant: "active",
      });
    } else {
      data.push({
        title: "Booked",
        showLine: false,
        variant: "inactive",
      });
    }
  }

  if (!data.length && directBookingLog) {
    data.push({
      title: "Booked",
      showLine: false,
      variant: "completed",
      children: [
        {
          title: directBookingLog.user_full_name,
          status: "booked",
          comment: directBookingLog?.comment,
          date: directBookingLog.timestamp,
        },
      ],
    });
  }

  return data;
};
